import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { useAuth } from '../context/AuthContext';
import api from '../api/client';
import { Info, Search, X, Loader2, Eye, EyeOff } from 'lucide-react';
import MoveDetailModal from './MoveDetailModal';


const TransitionMatrixView = () => {
  const { user, arsenal } = useAuth();
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [focusedMove, setFocusedMove] = useState('');
  const [matrix, setMatrix] = useState({});
  const [moves, setMoves] = useState([]);
  const [includeReverse, setIncludeReverse] = useState(false);
  const [selectedMove, setSelectedMove] = useState(null);
  const [arsenalOnly, setArsenalOnly] = useState(true);
  const [favoritesOnly, setFavoritesOnly] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const [searchInput, setSearchInput] = useState(''); // Valeur temporaire de recherche
  const [showMatrix, setShowMatrix] = useState(false); // Contrôle l'affichage de la matrice
  const [matrixSize, setMatrixSize] = useState(0); // Taille de la matrice pour l'utilisateur
  const searchCacheRef = useRef(new Map()); // Cache pour les suggestions (useRef pour éviter les re-rendus)

  // Index de recherche optimisé (inclut éventuellement les moves Otherside)
  const searchIndex = useMemo(() => {
    if (!user?.moves) return new Map();
    
    let moves = arsenalOnly 
      ? user.moves.filter(m => arsenal.includes(m.id))
      : user.moves;
    
    if (selectedTypes.length > 0) {
      moves = moves.filter(m => selectedTypes.includes(m.type));
    }
    
    // Créer un index par préfixe pour une recherche ultra-rapide
    const index = new Map();
    
    moves.forEach(move => {
      const baseName = move.name;
      const baseLower = baseName.toLowerCase();

      // Indexer le move normal
      for (let len = 2; len <= Math.min(4, baseLower.length); len++) {
        const prefix = baseLower.substring(0, len);
        if (!index.has(prefix)) {
          index.set(prefix, []);
        }
        index.get(prefix).push({ name: baseName, type: move.type });
      }

      // Indexer aussi la variante Otherside si demandée
      if (includeReverse) {
        const reverseName = `Otherside ${baseName}`;
        const reverseLower = reverseName.toLowerCase();
        for (let len = 2; len <= Math.min(4, reverseLower.length); len++) {
          const prefix = reverseLower.substring(0, len);
          if (!index.has(prefix)) {
            index.set(prefix, []);
          }
          index.get(prefix).push({ name: reverseName, type: move.type });
        }
      }
    });
    
    return index;
  }, [user?.moves, arsenalOnly, arsenal, selectedTypes, includeReverse]);

  // Liste unique des catégories de moves de l'utilisateur
  const categories = useMemo(() => 
    Array.from(new Set((user?.moves || []).filter(m => arsenal.includes(m.id)).map(m => m.type))),
    [user?.moves, arsenal]
  );

  // Gestion de la sélection multiple de types
  const handleTypeToggle = useCallback((type) => {
    setSelectedTypes(prev =>
      prev.includes(type)
        ? prev.filter(t => t !== type)
        : [...prev, type]
    );
  }, []);

  // Fonction de recherche avec index, mais filtrage par sous-chaîne sur les noms complets
  const handleSearchChange = useCallback(
    (value) => {
      setSearchInput(value);

      const raw = value.trim();
      if (!raw || searchIndex.size === 0) {
        setSearchSuggestions([]);
        return;
      }

      const searchTerm = raw.toLowerCase();

      // Construire un pool unique de moves à partir de l'index
      // (inclut les variants Otherside si includeReverse === true)
      const poolMap = new Map();
      for (const [, moves] of searchIndex) {
        moves.forEach((m) => {
          if (!poolMap.has(m.name)) {
            poolMap.set(m.name, m);
          }
        });
      }
      const pool = Array.from(poolMap.values());

      // Filtrer par sous-chaîne dans le nom complet (peu importe la longueur)
      const suggestions = pool
        .filter((m) => m.name.toLowerCase().includes(searchTerm))
        .slice(0, 6);

      searchCacheRef.current.set(searchTerm, suggestions);
      setSearchSuggestions(suggestions);
    },
    [searchIndex]
  );

  // Fonction pour sélectionner un move (met à jour la matrice)
  const handleSelectMove = useCallback((moveName) => {
    setFocusedMove(moveName);
    setSearchInput(moveName);
    setSearchSuggestions([]); // Clear les suggestions
  }, []);

  // Récupère les moves pour la matrice focalisée
  useEffect(() => {
    if (!user?.moves) return;
    
    setIsLoading(true);
    
    // Utilise setTimeout pour permettre au loading de s'afficher
    setTimeout(() => {
      // Commence par tous les moves ou seulement l'arsenal selon le filtre
      let allMoves = arsenalOnly 
        ? user.moves.filter(m => arsenal.includes(m.id))
        : user.moves;
      
      // Filtre par type si des types sont sélectionnés
      if (selectedTypes.length > 0) {
        allMoves = allMoves.filter(m => selectedTypes.includes(m.type));
      }
      
      // Convertit en noms de moves
      let moveNames = allMoves.map(m => m.name);
      
      // Ajoute les moves "Otherside" si demandé
      if (includeReverse) {
        moveNames = [...moveNames, ...moveNames.map(m => `Otherside ${m}`)];
      }
      
      // Si un move focalisé est sélectionné, s'assure qu'il est inclus et placé en premier
      if (focusedMove) {
        // S'assure que le move focalisé est toujours inclus, même s'il ne correspond pas aux filtres
        if (!moveNames.includes(focusedMove)) {
          // Vérifie si le move focalisé existe dans les moves de l'utilisateur
          const focusedMoveData = user.moves.find(m => m.name === focusedMove);
          if (focusedMoveData) {
            moveNames = [focusedMove, ...moveNames];
          }
        } else {
          // Place le move focalisé en premier
          moveNames = [focusedMove, ...moveNames.filter(m => m !== focusedMove)];
        }
      }
      
      setMoves(moveNames);
      setMatrixSize(moveNames.length);
      setIsLoading(false);
    }, 100); // Petit délai pour montrer le loading
  }, [user, selectedTypes, includeReverse, focusedMove, arsenalOnly, arsenal]);

  // Nettoyer le cache quand les filtres changent
  useEffect(() => {
    searchCacheRef.current.clear();
    setSearchSuggestions([]);
  }, [arsenalOnly, selectedTypes]);






  // Charge la matrice depuis l'API
  useEffect(() => {
    const loadTransitionMatrix = async () => {
      try {
        const { data } = await api.get('/me/transition-matrix');
        const savedMatrix = data.transitionMatrix || {};
        
        // Pré-cocher les cases diagonales pour les moves de l'arsenal
        const updatedMatrix = { ...savedMatrix };
        moves.forEach(moveName => {
          const moveData = findMoveByName(moveName);
          if (moveData && arsenal.includes(moveData.id)) {
            updatedMatrix[`${moveName}|${moveName}`] = true;
          }
        });
        
        setMatrix(updatedMatrix);
      } catch (error) {
        console.error('Failed to load transition matrix:', error);
        // Fallback: initialiser avec les cases diagonales cochées pour l'arsenal
        const initialMatrix = {};
        moves.forEach(moveName => {
          const moveData = findMoveByName(moveName);
          if (moveData && arsenal.includes(moveData.id)) {
            initialMatrix[`${moveName}|${moveName}`] = true;
          }
        });
        setMatrix(initialMatrix);
      }
    };

    if (moves.length > 0) {
      loadTransitionMatrix();
    }
  }, [moves.length, arsenal, arsenalOnly]);

  // Sauvegarde la matrice à chaque modification
  useEffect(() => {
    const saveTransitionMatrix = async () => {
      try {
        await api.patch('/me/transition-matrix', { transitionMatrix: matrix });
      } catch (error) {
        console.error('Failed to save transition matrix:', error);
      }
    };

    // Délai pour éviter trop d'appels API
    const timeoutId = setTimeout(saveTransitionMatrix, 1000);
    return () => clearTimeout(timeoutId);
  }, [matrix]);

  const findMoveByName = useCallback((moveName) => {
    // Enlever le préfixe "reverse" si présent
    const cleanName = moveName.replace(/^Otherside\s+/i, '');
    return user.moves.find(move => move.name.toLowerCase() === cleanName.toLowerCase());
  }, [user?.moves]);

  const handleToggle = useCallback((from, to) => {
    // Empêcher de décocher les cases diagonales des moves de l'arsenal
    if (from === to) {
      const moveData = findMoveByName(from);
      if (moveData && arsenal.includes(moveData.id)) {
        return; // Ne pas permettre de décocher la maîtrise d'un move de l'arsenal
      }
    }
    
    setMatrix(prev => ({
      ...prev,
      [`${from}|${to}`]: !prev[`${from}|${to}`]
    }));
  }, [findMoveByName, arsenal]);

  // Obtenir le nombre total de moves disponibles (pour les indicateurs)
  const getTotalAvailableMoves = () => {
    if (!user?.moves) return 0;
    let allMoves = arsenalOnly 
      ? user.moves.filter(m => arsenal.includes(m.id))
      : user.moves;
    if (selectedTypes.length > 0) {
      allMoves = allMoves.filter(m => selectedTypes.includes(m.type));
    }
    return allMoves.length;
  };

  return (
    <div className="min-h-screen bg-black text-white flex flex-col items-center py-10">
      <style jsx>{`
        .matrix-container::-webkit-scrollbar {
          height: 12px;
        }
        .matrix-container::-webkit-scrollbar-track {
          background: #1f1f1f;
          border-radius: 6px;
        }
        .matrix-container::-webkit-scrollbar-thumb {
          background: #ffffff;
          border-radius: 6px;
        }
        .matrix-container::-webkit-scrollbar-thumb:hover {
          background: #cccccc;
        }
      `}</style>
      {arsenal.length === 0 && (
        <div className="bg-yellow-100 text-yellow-900 border-l-4 border-yellow-500 p-4 rounded max-w-2xl mx-auto mb-8 text-center font-bold">
          To use the transition matrix, you must first add moves to your arsenal from the Breaking Encyclopedia.
        </div>
      )}
      <Card className="w-[85%] min-w-[85%] border-4 bg-white bg-black">
        <CardHeader className="border-b-2 bg-white">
          <CardTitle className="text-white font-black text-2xl text-center">Transition Matrix</CardTitle>
          </CardHeader>
            
        <CardContent className="p-6">
          {/* Focus Move Selection */}
          <div className="mb-6">
            <div className="text-center mb-4">
              <h3 className="text-lg font-bold text-white mb-2">Select Focus Move (Optional)</h3>
              <p className="text-gray-400 text-sm">Choose a move to analyze its transitions with other moves, or leave empty to see all moves</p>
            </div>
            
            <div className="relative max-w-md mx-auto">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                type="text"
                placeholder="Search for a move (e.g., Flare, Headspin, Cross Step...)"
                value={searchInput}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="pl-10 pr-10 bg-gray-800 border-white text-white placeholder-gray-400 focus:ring-2 focus:ring-white focus:border-white"
              />
              {searchInput && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setSearchInput('');
                    setFocusedMove('');
                    setSearchSuggestions([]);
                  }}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 h-6 w-6 p-0 text-gray-400 hover:text-white"
                >
                  <X className="w-4 h-4" />
                </Button>
              )}
            </div>
            
            {/* Focus Move Suggestions */}
            {searchSuggestions.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2 justify-center">
                <span className="text-gray-400 text-sm">Suggestions:</span>
                {searchSuggestions.map((move, index) => (
                  <Badge
                    key={index}
                    className={`cursor-pointer px-2 py-1 text-xs bg-white-700 text-white hover:bg-gray-600`}
                    onClick={() => handleSelectMove(move.name)}
                  >
                    {move.name} ({move.type})
                  </Badge>
                ))}
              </div>
            )}
            
          </div>

          {/* Type Filters */}
          <div className="mb-6 flex flex-wrap gap-3 items-center justify-center">
            {categories.map(cat => (
              <Badge
                key={cat}
                className={`cursor-pointer px-4 py-2 border-2 font-bold text-sm ${selectedTypes.includes(cat) ? 'bg-white text-black border-black' : 'bg-gray-800 text-white border-white hover:bg-gray-700'}`}
                onClick={() => handleTypeToggle(cat)}
              >
                {cat.toUpperCase()}
              </Badge>
            ))}
            
            {/* Bouton pour effacer tous les filtres */}
            {selectedTypes.length > 0 && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  setSelectedTypes([]);
                }}
                className="border-red-500 text-red-500 hover:bg-red-500 hover:text-white"
              >
                Clear Filters
              </Button>
            )}
          </div>
          <div className="mb-6 flex flex-col gap-3 items-center justify-center">
            <div className="flex items-center gap-3 justify-center">
              <Checkbox
                checked={includeReverse}
                onCheckedChange={setIncludeReverse}
                className="border-white data-[state=checked]:bg-white data-[state=checked]:border-white"
              />
              <span className="text-white font-medium">Include Otherside moves</span>
            </div>
            <div className="flex items-center gap-3 justify-center">
              <Checkbox
                checked={arsenalOnly}
                onCheckedChange={setArsenalOnly}
                className="border-white data-[state=checked]:bg-white data-[state=checked]:border-white"
              />
              <span className="text-white font-medium">Only Arsenal Moves</span>
            </div>
            <div className="flex items-center gap-3 justify-center">
              <Checkbox
                checked={favoritesOnly}
                onCheckedChange={setFavoritesOnly}
                className="border-white data-[state=checked]:bg-yellow-300 data-[state=checked]:border-yellow-300"
              />
              <span className="text-white font-medium">Only Favorite Transitions</span>
            </div>
          </div>
                      
          <div className="text-xs text-gray-400 mt-4 text-center">
            Diagonal = mastery of the move itself. Off-diagonal = transition from the row move to the column move.
          </div>
          
          {/* Loading Indicator */}
          {isLoading && (
            <div className="flex justify-center items-center py-8">
              <div className="flex items-center gap-3 text-white">
                <Loader2 className="w-6 h-6 animate-spin" />
                <span className="text-lg">Loading transition matrix...</span>
              </div>
            </div>
          )}
          
          {/* Performance Warning and Controls */}
          {!isLoading && moves.length > 0 && (
            <div className="text-center mb-4">
              <div className="text-gray-400 text-xs mb-2">
                {moves.length} moves selected ({moves.length * moves.length} total transitions)
              </div>
              
              {matrixSize > 50 && !showMatrix && (
                <div className="bg-yellow-900 border border-yellow-600 text-yellow-200 p-4 rounded-lg mb-4">
                  <div className="flex items-center justify-center gap-2 mb-2">
                    <EyeOff className="w-5 h-5" />
                    <span className="font-bold">Large Matrix Warning</span>
                  </div>
                  <p className="text-sm mb-3">
                    This will create {matrixSize * matrixSize} checkboxes ({matrixSize * matrixSize * 2} DOM elements). 
                    This may impact performance.
                  </p>
                  <Button
                    onClick={() => setShowMatrix(true)}
                    className="bg-yellow-600 hover:bg-yellow-500 text-black font-bold"
                  >
                    <Eye className="w-4 h-4 mr-2" />
                    Show Matrix ({matrixSize}x{matrixSize})
                  </Button>
                </div>
              )}
              
              {matrixSize <= 50 && (
                <div className="text-green-400 text-xs mb-2">
                  ✓ Matrix size is manageable ({matrixSize}x{matrixSize})
                </div>
              )}
            </div>
          )}
          
          {isLoading ? (
            <div className="text-center text-gray-400 py-10">
              <div className="flex justify-center items-center gap-3 mb-4">
                <Loader2 className="w-6 h-6 animate-spin" />
                <span>Preparing matrix...</span>
              </div>
            </div>
          ) : moves.length === 0 ? (
            <div className="text-center text-gray-400 py-10">
              {!focusedMove && selectedTypes.length === 0 ? (
                'No moves in your arsenal. Add moves from the Breaking Encyclopedia first.'
              ) : (
                'No moves in the selected categories.'
              )}
            </div>
          ) : (showMatrix || matrixSize <= 50) ? (
            <div className="overflow-x-auto h-[85vh] w-full matrix-container" style={{ scrollbarWidth: 'thin', scrollbarColor: 'white black', minWidth: '100%' }}>
              <table className="min-w-full border border-white">
                <thead className="sticky top-0 z-10 bg-black">
                  <tr>
                    <th className="border border-white bg-gray-900 text-white p-2 text-center sticky left-0 z-20">
                      <div className="text-xs font-bold">FROM</div>
                      {focusedMove && <div className="text-xs text-gray-400">(Focus: {focusedMove})</div>}
                    </th>
                    {moves.map((move, idx) => {
                      const moveData = findMoveByName(move);
                      const isFocused = move === focusedMove;
                      return (
                        <th key={idx} className={`border border-white text-white p-2 text-xs relative group ${isFocused ? 'bg-blue-900' : 'bg-gray-900'}`}>
                          <div className="flex items-center justify-between">
                            <span className={`truncate ${isFocused ? 'font-bold' : ''}`}>{move}</span>
                            {moveData && (
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-6 w-6 p-0 text-white hover:text-black hover:bg-white transition-all opacity-0 group-hover:opacity-100 ml-1"
                                onClick={() => setSelectedMove(moveData)}
                                title="View move details"
                              >
                                <Info className="w-3 h-3" />
                              </Button>
                            )}
                          </div>
                          {isFocused && <div className="text-xs text-blue-300 mt-1">FOCUS</div>}
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {moves.map((from, i) => {
                    const fromMoveData = findMoveByName(from);
                    const isFocused = from === focusedMove;
                    return (
                      <tr key={i}>
                        <td className={`border border-white text-white p-2 text-xs font-bold relative group ${isFocused ? 'bg-blue-900' : 'bg-gray-900'} sticky left-0 z-20`}>
                          <div className="flex items-center justify-between">
                            <span className={`truncate ${isFocused ? 'font-bold' : ''}`}>{from}</span>
                            {fromMoveData && (
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-6 w-6 p-0 text-white hover:text-black hover:bg-white transition-all opacity-0 group-hover:opacity-100 ml-1"
                                onClick={() => setSelectedMove(fromMoveData)}
                                title="View move details"
                              >
                                <Info className="w-3 h-3" />
                              </Button>
                            )}
                          </div>
                          {isFocused && <div className="text-xs text-blue-300 mt-1">FOCUS</div>}
                        </td>
                        {moves.map((to, j) => {
                          const key = `${from}|${to}`;
                          const isChecked = !!matrix[key];
                          const favKey = `${from}|${to}|fav`;
                          const isFavorite = !!matrix[favKey];

                          // Si on est en mode "only favorites", ne montrer que les transitions marquées
                          if (favoritesOnly && !isFavorite) {
                            return (
                              <td key={j} className="border border-white bg-black text-center text-gray-700 text-xs">
                                {/* Cellule vide / non favorite */}
                              </td>
                            );
                          }

                          return (
                            <td key={j} className="border border-white bg-black text-center">
                              <div className="flex items-center justify-center gap-1">
                                <Checkbox
                                  checked={isChecked}
                                  onCheckedChange={() => handleToggle(from, to)}
                                  className="border-white data-[state=checked]:bg-white data-[state=checked]:border-white"
                                  disabled={isLoading} // Désactiver pendant le chargement
                                />
                                {/* Petite étoile pour marquer une transition favorite */}
                                <button
                                  type="button"
                                  onClick={() =>
                                    setMatrix(prev => ({
                                      ...prev,
                                      [favKey]: !prev[favKey],
                                    }))
                                  }
                                  className={`text-xs ${
                                    isFavorite ? 'text-yellow-300' : 'text-gray-500'
                                  } hover:text-yellow-300`}
                                  title={isFavorite ? 'Unmark favorite transition' : 'Mark as favorite transition'}
                                  disabled={isLoading}
                                >
                                  ★
                                </button>
                              </div>
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center text-gray-400 py-10">
              <div className="flex items-center justify-center gap-2 mb-4">
                <EyeOff className="w-6 h-6" />
                <span>Matrix hidden for performance</span>
              </div>
              <p className="text-sm mb-4">
                Click "Show Matrix" above to display the {matrixSize}x{matrixSize} transition matrix.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Move Detail Modal */}
      <MoveDetailModal 
        move={selectedMove}
        isOpen={!!selectedMove}
        onClose={() => setSelectedMove(null)}
      />
    </div>
  );
};

export default TransitionMatrixView; 